/**
 * Build Explorer helpers — systems, focus paths, search expansion.
 * Built on modificationHierarchy forests; uses stable mod IDs (not array indices).
 */

import {
  buildCategoryForest,
  buildModificationForest,
  countChildren,
  type HierarchyModLike,
  type HierarchyNode,
} from './modificationHierarchy';

export type ExplorerGuideMode = 'all' | 'detailed' | 'with_media' | 'newest';

export interface ExplorerSystemSummary {
  category: string;
  partCount: number;
  assemblyCount: number;
  rootCount: number;
  highlightTitle?: string;
  imageUrl?: string | null;
}

export function explorerNodeId<T extends HierarchyModLike>(node: HierarchyNode<T>): string {
  if (node.mod.id !== undefined && node.mod.id !== null && String(node.mod.id) !== '') {
    return String(node.mod.id);
  }
  if (node.mod.temporary_id) return `t:${node.mod.temporary_id}`;
  return `i:${node.index}`;
}

export function countAssemblies<T extends HierarchyModLike>(node: HierarchyNode<T>): number {
  const self = node.children.length > 0 ? 1 : 0;
  return self + node.children.reduce((sum, c) => sum + countAssemblies(c), 0);
}

export function findNodeById<T extends HierarchyModLike>(
  forest: HierarchyNode<T>[],
  id: string
): HierarchyNode<T> | null {
  for (const node of forest) {
    if (explorerNodeId(node) === id) return node;
    const found = findNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}

/** Ancestor IDs from root → parent (excludes focus id). */
export function getAncestorIds<T extends HierarchyModLike>(
  forest: HierarchyNode<T>[],
  id: string
): string[] {
  const path: string[] = [];
  const walk = (nodes: HierarchyNode<T>[], trail: string[]): boolean => {
    for (const node of nodes) {
      const nid = explorerNodeId(node);
      if (nid === id) {
        path.push(...trail);
        return true;
      }
      if (walk(node.children, [...trail, nid])) return true;
    }
    return false;
  };
  walk(forest, []);
  return path;
}

export function getBreadcrumbTitles<T extends HierarchyModLike>(
  forest: HierarchyNode<T>[],
  id: string
): string[] {
  const titles: string[] = [];
  const walk = (nodes: HierarchyNode<T>[], trail: string[]): boolean => {
    for (const node of nodes) {
      const title = (node.mod.title || 'Part').trim() || 'Part';
      const next = [...trail, title];
      if (explorerNodeId(node) === id) {
        titles.push(...next);
        return true;
      }
      if (walk(node.children, next)) return true;
    }
    return false;
  };
  walk(forest, []);
  return titles;
}

export function buildSystemSummaries<T extends HierarchyModLike>(mods: T[]): ExplorerSystemSummary[] {
  const groups = buildCategoryForest(mods);
  return groups.map((g) => {
    let assemblyCount = 0;
    let highlightTitle: string | undefined;
    let bestKids = -1;
    let imageUrl: string | null | undefined;
    g.roots.forEach((root) => {
      assemblyCount += countAssemblies(root);
      const kids = countChildren(root);
      if (kids > bestKids) {
        bestKids = kids;
        highlightTitle = root.mod.title;
      }
      const img = (root.mod as { image_url?: string | null }).image_url;
      if (!imageUrl && img) imageUrl = img;
    });
    return {
      category: g.category,
      partCount: g.itemCount,
      assemblyCount,
      rootCount: g.roots.length,
      highlightTitle,
      imageUrl,
    };
  });
}

export interface ExplorerSearchHit {
  id: string;
  title: string;
  category: string;
  brand?: string;
  pathTitles: string[];
  ancestorIds: string[];
}

function specsText(specifications: unknown): string {
  if (!specifications) return '';
  if (typeof specifications === 'string') return specifications;
  if (Array.isArray(specifications)) return specifications.join(' ');
  if (typeof specifications === 'object') {
    return Object.entries(specifications as Record<string, unknown>)
      .map(([k, v]) => `${k} ${v}`)
      .join(' ');
  }
  return '';
}

export function searchExplorerHits<T extends HierarchyModLike & {
  description?: string | null;
  notes?: string | null;
  vendor?: string | null;
  specifications?: unknown;
}>(mods: T[], query: string, limit = 40): ExplorerSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const forest = buildModificationForest(mods);
  const hits: ExplorerSearchHit[] = [];

  const visit = (node: HierarchyNode<T>) => {
    const m = node.mod;
    const hay = [
      m.title,
      m.description || '',
      m.notes || '',
      m.category || '',
      m.vendor || '',
      specsText(m.specifications),
    ]
      .join(' ')
      .toLowerCase();
    if (hay.includes(q)) {
      const id = explorerNodeId(node);
      hits.push({
        id,
        title: (m.title || 'Part').trim() || 'Part',
        category: (m.category || 'Miscellaneous').trim() || 'Miscellaneous',
        brand: m.vendor || undefined,
        pathTitles: getBreadcrumbTitles(forest, id),
        ancestorIds: getAncestorIds(forest, id),
      });
    }
    node.children.forEach(visit);
  };
  forest.forEach(visit);
  return hits.slice(0, limit);
}

/** Roots for a category, or full forest when category is null/"All". */
export function forestForSystem<T extends HierarchyModLike>(
  mods: T[],
  category: string | null
): HierarchyNode<T>[] {
  if (!category || category === 'All') {
    return buildModificationForest(mods);
  }
  const groups = buildCategoryForest(mods);
  const group = groups.find((g) => g.category === category);
  return group?.roots ?? [];
}

/**
 * When a filter returns leaf matches, re-include ancestors so the explorer
 * can still show assembly containment.
 */
export function expandModsWithAncestors<T extends HierarchyModLike>(
  allMods: T[],
  matched: T[]
): T[] {
  if (matched.length === 0 || matched.length === allMods.length) return matched;
  const forest = buildModificationForest(allMods);
  const keep = new Set<string>();
  matched.forEach((m, i) => {
    const id =
      m.id !== undefined && m.id !== null && String(m.id) !== ''
        ? String(m.id)
        : m.temporary_id
          ? `t:${m.temporary_id}`
          : `i:${allMods.indexOf(m) >= 0 ? allMods.indexOf(m) : i}`;
    keep.add(id);
    getAncestorIds(forest, id).forEach((a) => keep.add(a));
  });
  return allMods.filter((m, i) => {
    const id =
      m.id !== undefined && m.id !== null && String(m.id) !== ''
        ? String(m.id)
        : m.temporary_id
          ? `t:${m.temporary_id}`
          : `i:${i}`;
    return keep.has(id);
  });
}

export function filterModsForGuide<T extends HierarchyModLike & {
  image_url?: string | null;
  install_date?: string | null;
  id?: string | number;
}>(mods: T[], mode: ExplorerGuideMode): T[] {
  if (mode === 'all') return mods;
  const forest = buildModificationForest(mods);
  const keep = new Set<string>();

  if (mode === 'with_media') {
    mods.forEach((m, i) => {
      if (m.image_url) {
        const id =
          m.id !== undefined && m.id !== null && String(m.id) !== ''
            ? String(m.id)
            : m.temporary_id
              ? `t:${m.temporary_id}`
              : `i:${i}`;
        keep.add(id);
        // Keep ancestors so hierarchy remains coherent
        const node = findNodeById(forest, id);
        if (node) {
          getAncestorIds(forest, id).forEach((a) => keep.add(a));
        }
      }
    });
  } else if (mode === 'newest') {
    const dated = [...mods]
      .filter((m) => m.install_date)
      .sort((a, b) => {
        const da = a.install_date ? new Date(a.install_date).getTime() : 0;
        const db = b.install_date ? new Date(b.install_date).getTime() : 0;
        return db - da;
      })
      .slice(0, 24);
    dated.forEach((m, i) => {
      const id =
        m.id !== undefined && m.id !== null && String(m.id) !== ''
          ? String(m.id)
          : m.temporary_id
            ? `t:${m.temporary_id}`
            : `i:${mods.indexOf(m) >= 0 ? mods.indexOf(m) : i}`;
      keep.add(id);
      getAncestorIds(forest, id).forEach((a) => keep.add(a));
    });
  } else if (mode === 'detailed') {
    const scored: Array<{ id: string; score: number }> = [];
    const visit = (node: HierarchyNode<T>) => {
      const kids = countChildren(node);
      if (kids >= 2) {
        scored.push({ id: explorerNodeId(node), score: kids });
      }
      node.children.forEach(visit);
    };
    forest.forEach(visit);
    scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .forEach((s) => {
        keep.add(s.id);
        getAncestorIds(forest, s.id).forEach((a) => keep.add(a));
        const node = findNodeById(forest, s.id);
        if (node) {
          const collect = (n: HierarchyNode<T>) => {
            keep.add(explorerNodeId(n));
            n.children.forEach(collect);
          };
          collect(node);
        }
      });
  }

  if (keep.size === 0) return mods;
  return mods.filter((m, i) => {
    const id =
      m.id !== undefined && m.id !== null && String(m.id) !== ''
        ? String(m.id)
        : m.temporary_id
          ? `t:${m.temporary_id}`
          : `i:${i}`;
    return keep.has(id);
  });
}
